<!-- interactive: whether this copy of the page lies flat in the book (and not on a turning sheet) so it can be drawn on and its stickers zoomed in on -->
<ng-template #pageTpl let-week let-interactive="interactive">
  @let tool = interactive ? drawingTool() : null;
  @let canZoom = interactive && !drawingTool();

  <div class="font-default pointer-events-none absolute top-6 left-0 w-1/2 text-center text-3xl text-black/80">Week {{ week + 1 }}</div>

  @for (entry of rewardsFor(week); track entry.uid) {
    <img
      class="absolute block w-auto object-contain shadow-sm"
      [class.pointer-events-none]="!canZoom"
      [class.cursor-pointer]="canZoom"
      [src]="$lpApiService.getAssetUrl(entry.reward.image)"
      [alt]="entry.reward.name"
      [attr.role]="canZoom ? 'button' : null"
      [attr.tabindex]="canZoom ? 0 : null"
      [style.left.px]="entry.placed.x"
      [style.top.px]="entry.placed.y"
      [style.z-index]="entry.zIndex"
      [style.height.px]="rewardHeightFor(entry.reward)"
      [style.rotate]="entry.placed.rotation + 'deg'"
      style="max-width: unset; transform: translate(-50%, -50%)"
      (click)="canZoom && zoomedReward.set(entry.reward)"
      (keydown.enter)="canZoom && zoomedReward.set(entry.reward)"
    />
  }

  <rid-scrapbook-drawing [week]="week" />
  @if (tool) {
    <rid-scrapbook-drawing-input [week]="week" [tool]="tool" />
  }
</ng-template>

<!-- a page of a turning sheet: a copy of the book sprite lined up with the real book (so the paper matches exactly), with the week's stickers on top -->
<ng-template #sheetPageTpl let-week>
  @let inset = bookContentInset();
  <div
    class="pointer-events-none absolute"
    [style.top.px]="-inset.top"
    [style.right.px]="-inset.right"
    [style.bottom.px]="-inset.bottom"
    [style.left.px]="-inset.left"
  >
    <app-nine-sliced-container
      [url]="bookUrl()"
      [size]="bookSize()"
      [contentBox]="bookContentBox()"
      [paddingBox]="bookPaddingBox()"
      [scale]="bookScale()"
    />
  </div>
  <ng-container *ngTemplateOutlet="pageTpl; context: { $implicit: week }" />
</ng-template>

<div class="relative size-full">
  <rid-modal-background [blur]="true" />

  <div class="absolute inset-0 grid place-items-center p-8">
    <div class="relative w-full max-w-4xl">
      @if (targetPageIndex() > 0) {
        <rid-chevron
          class="absolute top-1/2 -left-28 z-10 -translate-y-1/2 cursor-pointer"
          direction="LEFT"
          animate.enter="anim-pop-in"
          animate.leave="anim-pop-out"
          (click)="previousPage()"
        />
      }
      @if (targetPageIndex() < pageCount() - 1) {
        <rid-chevron
          class="absolute top-1/2 -right-28 z-10 -translate-y-1/2 cursor-pointer"
          direction="RIGHT"
          animate.enter="anim-pop-in"
          animate.leave="anim-pop-out"
          (click)="nextPage()"
        />
      }

      <div class="max-h-[80vh] w-full" style="aspect-ratio: 16 / 10">
        <app-nine-sliced-container
          [url]="bookUrl()"
          [size]="bookSize()"
          [contentBox]="bookContentBox()"
          [paddingBox]="bookPaddingBox()"
          [scale]="bookScale()"
        >
          <!-- everything below lives in the content box, so all layers share the same spine (its horizontal center) -->
          <div class="relative size-full">
            <!-- what lies below the turning sheets -->
            <div class="layer half-left">
              <ng-container *ngTemplateOutlet="pageTpl; context: { $implicit: leftPageIndex(), interactive: isBookStill() }" />
            </div>
            <div class="layer half-right">
              <ng-container *ngTemplateOutlet="pageTpl; context: { $implicit: rightPageIndex(), interactive: isBookStill() }" />
            </div>

            <!-- all sheets share one 3D context, so the browser sorts out which one lies on top -->
            <!-- tracked by index, so a sheet keeps its (already drawn) element when it starts or stops turning -->
            <div class="sheets">
              @for (sheet of renderedSheets(); track sheet.index) {
                <div
                  #sheetEl
                  class="sheet"
                  [class.turning]="!!sheet.turn"
                  [class.backward]="sheet.turn && !sheet.turn.startedForward"
                  [attr.data-sheet-index]="sheet.index"
                >
                  <div class="layer sheet-face sheet-front half-right">
                    <ng-container *ngTemplateOutlet="sheetPageTpl; context: { $implicit: sheet.index }" />
                  </div>
                  <div class="layer sheet-face sheet-back half-left">
                    <ng-container *ngTemplateOutlet="sheetPageTpl; context: { $implicit: sheet.index + 1 }" />
                  </div>
                </div>
              }
            </div>
          </div>
        </app-nine-sliced-container>
      </div>
    </div>
  </div>

  <rid-button class="absolute top-6 right-6 z-20" content="Sluiten" (click)="close()" />

  <!-- the bottom of the tools sticks out below the screen -->
  <rid-drawing-tray class="absolute -bottom-10 left-1/2 z-20 -translate-x-1/2" [(tool)]="drawingTool" />

  @if (zoomedReward(); as reward) {
    <div
      class="absolute inset-0 z-30 grid cursor-pointer place-items-center p-16"
      animate.enter="anim-fade-in"
      animate.leave="anim-fade-out"
      role="button"
      tabindex="0"
      aria-label="Sluiten"
      (click)="zoomedReward.set(null)"
      (keydown.enter)="zoomedReward.set(null)"
      (keydown.escape)="zoomedReward.set(null)"
    >
      <rid-modal-background [blur]="true" />
      <img
        class="relative h-[70vh] max-w-[80vw] object-contain drop-shadow-2xl"
        [src]="$lpApiService.getAssetUrl(reward.image)"
        [alt]="reward.name"
        animate.enter="anim-pop-in"
      />
    </div>
  }
</div>
